import { z } from 'zod';
import { UrlOrPath } from './url';

export const leaveStatuses = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export const LeaveStatus = z.enum(leaveStatuses);
export type LeaveStatus = z.infer<typeof LeaveStatus>;

export const leaveUnits = ['day', 'hour'] as const;
export const LeaveUnit = z.enum(leaveUnits);
export type LeaveUnit = z.infer<typeof LeaveUnit>;

export const attendanceStatuses = ['present', 'absent', 'on_leave'] as const;
export const AttendanceStatus = z.enum(attendanceStatuses);

/**
 * Where someone stands with the company. Someone who resigned is a different
 * HR record from someone let go, and people from other companies who help out
 * are kept in the directory too, as external.
 */
export const employmentStatuses = [
  'probation', 'active', 'notice_period', 'resigned', 'terminated', 'external',
] as const;
export const EmploymentStatus = z.enum(employmentStatuses);
export type EmploymentStatus = z.infer<typeof EmploymentStatus>;

/** The employee directory's sections, each a set of statuses. */
export const directorySections = {
  current: ['probation', 'active', 'notice_period'],
  former: ['resigned'],
  terminated: ['terminated'],
  external: ['external'],
} as const satisfies Record<string, readonly EmploymentStatus[]>;
export type DirectorySection = keyof typeof directorySections;
export const DirectorySection = z.enum(['current', 'former', 'terminated', 'external']);

/** The group's companies; one person can work for more than one. */
export const companies = ['hostsailor', 'ipv4mall', 'digitised'] as const;
export const Company = z.enum(companies);
export type Company = z.infer<typeof Company>;
/** Brand names, the same in every language. */
export const companyNames: Record<Company, string> = {
  hostsailor: 'HostSailor',
  ipv4mall: 'IPv4 Mall',
  digitised: 'Digitised',
};

export const CreateLeaveRequest = z
  .object({
    leaveTypeId: z.string().uuid(),
    unit: LeaveUnit.default('day'),
    startDate: z.string().date(),
    endDate: z.string().date(),
    startTime: z.string().time().optional(),
    endTime: z.string().time().optional(),
    reason: z.string().max(2000).optional(),
    attachmentUrl: UrlOrPath.optional(),
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  })
  .refine((v) => v.unit === 'day' || (v.startTime && v.endTime), {
    message: 'hour-based requests need startTime and endTime',
    path: ['startTime'],
  });

export const DecideLeaveRequest = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejectionReason: z.string().max(1000).optional(),
}).refine((v) => v.decision !== 'rejected' || !!v.rejectionReason, {
  message: 'rejectionReason is required when rejecting',
  path: ['rejectionReason'],
});

export const CheckIn = z.object({ at: z.string().datetime().optional() });

export type CreateLeaveRequest = z.infer<typeof CreateLeaveRequest>;

// Departments: one per person, led by their owners.
export const SaveDepartment = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullish(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).nullish(),
  ownerIds: z.array(z.string().uuid()).max(50).optional(),
});
export const DepartmentMembers = z.object({ userIds: z.array(z.string().uuid()).max(2000) });
export const MergeDepartment = z.object({ intoId: z.string().uuid() });

export type SaveDepartment = z.infer<typeof SaveDepartment>;
